import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { loadStripe, Stripe } from '@stripe/stripe-js';
import { alpha, useTheme } from '@mui/material/styles';
import {
  VisaIcon,
  MastercardIcon,
  AmexIcon,
  DiscoverIcon,
  GenericIcon,
} from "react-svg-credit-card-payment-icons";
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import {
  Box,
  Button,
  Typography,
  CircularProgress,
  IconButton,
  Chip,
} from '@mui/material';
import {
  Add as AddIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import toast from 'react-hot-toast';
import useConfirmationDialog from 'src/components/ConfirmationDialog/view';
import {
  useGetStripeCustomerSecretsApi,
  useGetStripePublishableKeyApi,
  useGetStripePaymentMethodsApi,
  useDetachPaymentMethodApi,
  useSetDefaultPaymentMethodApi,
} from 'src/modules/companies/api/StripeApi';
import { StripePaymentMethod } from 'src/modules/companies/types/StripeApi';

/**
 * `default` matters as much as the rest to a flow that is about to charge: Stripe charges the customer's
 * default card, so changing which one that is changes what the surrounding screen should be saying.
 */
export type PaymentMethodsChange = 'loaded' | 'added' | 'removed' | 'default';

export type PaymentMethodsChangeEvent = {
  methods: StripePaymentMethod[];
  reason: PaymentMethodsChange;
};

/**
 * Turns the card form's own button into the surrounding flow's action: what it says, and what runs once the
 * card is attached and made the default. For a flow whose very next step is a charge — the signup funnel pays
 * the moment the card is in, and an "Add" button in front of that is a click that means nothing to the client.
 */
export type PaymentMethodsSubmit = {
  label: string;
  busyLabel: string;
  onSubmitted: () => void | Promise<void>;
};

type PaymentMethodsProps = {
  companyId: string;
  /** What the surrounding flow does once the cards on file change. */
  onChanged?: (event: PaymentMethodsChangeEvent) => void | Promise<void>;
  /**
   * Make every newly added card the default one, not just the first. For a flow that is about to charge the
   * card the client is adding right now: the charge goes to the customer's default, so leaving an older card
   * in that role means adding a card changes nothing and the same refusal comes back with no explanation.
   */
  makeNewCardDefault?: boolean;
  /**
   * The client has opened the form to enter another card. What the surrounding flow said about the previous
   * one — a refusal, most of all — is about to stop being true, and leaving it on screen next to a fresh
   * card form reads as a verdict on the card being typed.
   */
  onAddCardStarted?: () => void;
  /**
   * A charge is in flight. Stripe charges whichever card is the default when it is made, so every control
   * here — adding one included, under `makeNewCardDefault` — moves the money mid-charge. The list itself
   * stays on screen: it is what the surrounding flow's notice is read against.
   */
  locked?: boolean;
  submit?: PaymentMethodsSubmit;
  /** Told when the card form is closed without a card, for a host whose own action the form has taken over. */
  onAddCardCancelled?: () => void;
};

export const CardBrandIcon: React.FC<{ brand?: string }> = ({ brand }) => {
  const style = { width: '24px', height: '24px' };
  switch (brand) {
    case 'visa':
      return <VisaIcon format="flat" style={style} />;
    case 'mastercard':
      return <MastercardIcon format="flat" style={style} />;
    case 'amex':
      return <AmexIcon format="flat" style={style} />;
    case 'discover':
      return <DiscoverIcon format="flat" style={style} />;
    default:
      return <GenericIcon format="flat" style={{ width: '16px', height: '16px' }} />;
  }
};

const PaymentMethodItem: React.FC<{
  paymentMethod: StripePaymentMethod;
  isDefault: boolean;
  onRemove: (id: string) => void;
  onSetDefault: (id: string) => void;
  canRemove: boolean;
  locked?: boolean;
}> = ({ paymentMethod, isDefault, onRemove, onSetDefault, canRemove, locked }) => {
  const formatExpiration = (month: number, year: number) => {
    return `${String(month).padStart(2, '0')}/${String(year).slice(-2)}`;
  };

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        p: 2,
        border: '1px solid',
        borderColor: 'divider',
        mb: 1.5,
        bgcolor: '#fff',
        '&:hover': {
          borderColor: 'grey.400',
        },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1 }}>
        <CardBrandIcon brand={paymentMethod.card?.brand} />
        <Box sx={{ flex: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
            <Typography variant="body1" sx={{ fontWeight: 'fontWeightMedium', color: '#1a1f36' }}>
              {paymentMethod.card?.brand || 'Card'} •••• {paymentMethod.card?.last4 || ''}
            </Typography>
            {paymentMethod.card && (
              <Typography variant="body2" sx={{ color: 'grey.600', fontWeight: 'fontWeightRegular' }}>
                Expires {formatExpiration(paymentMethod.card.exp_month, paymentMethod.card.exp_year)}
              </Typography>
            )}
            {isDefault && (
              <Chip
                label="Default"
                size="small"
                sx={{
                  height: 20,
                  fontSize: '11px',
                  bgcolor: 'grey.100',
                  color: 'grey.600',
                  fontWeight: 'fontWeightMedium',
                }}
              />
            )}
          </Box>
        </Box>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {!isDefault && !locked && (
          <Button
            size="small"
            onClick={() => onSetDefault(paymentMethod.id)}
            sx={{
              fontSize: '13px',
              color: '#635bff',
              '&:hover': {
                bgcolor: 'rgba(99, 91, 255, 0.08)',
              },
            }}
          >
            Set as default
          </Button>
        )}
        {canRemove && !locked && (
          <IconButton
            size="small"
            aria-label="Remove payment method"
            onClick={() => onRemove(paymentMethod.id)}
            sx={{
              color: 'grey.600',
              '&:hover': {
                color: 'error.main',
                bgcolor: theme => alpha(theme.palette.error.main, 0.08),
              },
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        )}
      </Box>
    </Box>
  );
};

const AddPaymentMethodForm: React.FC<{
  onSuccess: () => void | Promise<void>;
  onSetAsDefault?: (paymentMethodId: string) => Promise<void>;
  hasDefaultPaymentMethod: boolean;
  makeNewCardDefault?: boolean;
  submit?: PaymentMethodsSubmit;
  /** Closes the form and leaves the cards on file as they were. Absent where there are none to go back to. */
  onCancel?: () => void;
}> = ({ onSuccess, onSetAsDefault, hasDefaultPaymentMethod, makeNewCardDefault, submit, onCancel }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState<string>('');

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    setIsLoading(true);
    setMessage('');

    try {
      const result = await stripe.confirmSetup({
        elements,
        confirmParams: {
          return_url: window.location.href,
          payment_method_data: {
            billing_details: {
              name: '',
            },
          },
        },
        redirect: 'if_required',
      });

      if (result.error) {
        console.error('Stripe error:', result.error);
        setMessage(result.error.message || 'An error occurred.');
        setIsLoading(false);
        return;
      }

      if (!result.setupIntent) {
        console.error('No setupIntent in result');
        setMessage('Setup intent was not created. Please try again.');
        setIsLoading(false);
        return;
      }

      const setupIntent = result.setupIntent;

      const paymentMethodId = typeof setupIntent.payment_method === 'string'
        ? setupIntent.payment_method
        : setupIntent.payment_method?.id;

      if (!paymentMethodId) {
        console.error('No payment method ID found in setupIntent');
        setMessage('Payment method was not created. Please try again.');
        setIsLoading(false);
        return;
      }

      if ((!hasDefaultPaymentMethod || makeNewCardDefault) && onSetAsDefault) {
        try {
          await onSetAsDefault(paymentMethodId);
        } catch (err) {
          console.error('Failed to set as default:', err);
          // A charge is what happens next in submit mode, and it goes to whichever card is the default. With
          // this one not made it, paying now would charge something the client did not choose — or nothing.
          if (submit != null) {
            setMessage('Your card was saved but could not be selected for the charge. Please try again.');
            setIsLoading(false);
            return;
          }
          toast.error('Payment method added, but failed to set as default');
        }
      }

      if (submit != null) {
        // `onSuccess` closes this form, so the host's action runs with the form already gone: whatever it has
        // to report, it reports on its own screen. A throw from it would land in the catch below and write a
        // message nobody is looking at, which is why the signup screen's own pay and retry never throw.
        await onSuccess();
        await submit.onSubmitted();
        return;
      }

      toast.success('Payment method added successfully!');
      onSuccess();
    } catch (err) {
      console.error('Error adding payment method:', err);
      const errorMessage = err instanceof Error ? err.message : 'An unexpected error occurred.';
      setMessage(errorMessage);
      setIsLoading(false);
    }
  };

  return (
    <Box
      component="form"
      onSubmit={handleSubmit}
      sx={{
        p: 4,
        bgcolor: '#fff',
        border: '1px solid',
        borderColor: 'divider',
        /*
          No width of its own, exactly as the cards above have none: the app sets no `box-sizing`, so an
          explicit `width: 100%` is the width of the content and these paddings are added to it — which put
          the right border 66px past the card borders and a scrollbar under the page.
        */
      }}
    >
      <Box sx={{ mb: 3, width: '100%' }}>
        <PaymentElement
          options={{
            layout: 'auto',
            // Link is a card wallet, so the SetupIntent's `payment_method_types: ['card']` does not reach it:
            // left on, it hands back a method of type `link`, which the card list does not read.
            wallets: { link: 'never' },
          }}
        />
      </Box>

      {message && (
        <Typography
          variant="body2"
          color={message.includes('successful') ? 'success.main' : 'error.main'}
          sx={{ mb: 2 }}
        >
          {message}
        </Typography>
      )}

      <Box sx={{ display: 'flex', width: '100%', gap: 2, mt: 3 }}>
        <Button
          type="submit"
          variant="contained"
          disabled={!stripe || isLoading}
          sx={{
            flex: 1,
            bgcolor: '#635bff',
            color: '#fff',
            fontWeight: 'fontWeightMedium',
            py: 1.25,
            fontSize: '14px',
            '&:hover': {
              bgcolor: '#5851ea',
            },
            '&:disabled': {
              bgcolor: 'grey.300',
              color: 'grey.500',
            },
          }}
        >
          {isLoading
            ? (submit?.busyLabel ?? 'Adding...')
            : (submit?.label ?? 'Add')}
        </Button>
        {/*
          The way back out. Without it the form is a one-way door: opening it is a click, and closing it was
          only ever a card added — which strands a client who has one on file already and pressed Add by
          mistake, on a screen whose own action the form has taken over.
        */}
        {onCancel != null && (
          <Button
            variant="text"
            disabled={isLoading}
            onClick={onCancel}
            sx={{ fontWeight: 'fontWeightMedium', fontSize: '14px' }}
          >
            Cancel
          </Button>
        )}
      </Box>
    </Box>
  );
};

const PaymentMethodsList: React.FC<{
  paymentMethods: StripePaymentMethod[];
  onDefaultChanged: () => void;
  onPaymentMethodRemoved: () => void;
  onPaymentMethodAdded: () => Promise<void>;
  companyId: string;
  loading: boolean;
  stripePublishableKey: string | null;
  makeNewCardDefault?: boolean;
  onAddCardStarted?: () => void;
  onAddCardCancelled?: () => void;
  locked?: boolean;
  submit?: PaymentMethodsSubmit;
}> = ({ paymentMethods, onDefaultChanged, onPaymentMethodRemoved, onPaymentMethodAdded, companyId, stripePublishableKey, loading, makeNewCardDefault, onAddCardStarted, onAddCardCancelled, locked, submit }) => {
  const theme = useTheme();
  const hasDefaultPaymentMethod = paymentMethods.some(pm => pm.isDefault);
  const [showAddForm, setShowAddForm] = useState(paymentMethods.length === 0);
  const [setupIntentSecret, setSetupIntentSecret] = useState<string | null>(null);
  const [stripePromise, setStripePromise] = useState<Promise<Stripe | null> | null>(null);

  const getStripeCustomerSecrets = useGetStripeCustomerSecretsApi();
  const detachPaymentMethod = useDetachPaymentMethodApi();
  const setDefaultPaymentMethod = useSetDefaultPaymentMethodApi();
  const { openDialog, ConfirmationDialog } = useConfirmationDialog();

  useEffect(() => {
    if (stripePublishableKey && !stripePromise) {
      setStripePromise(loadStripe(stripePublishableKey));
    }
  }, [stripePublishableKey, stripePromise]);

  const fetchSetupIntent = useCallback(async () => {
    try {
      const response = await getStripeCustomerSecrets(companyId);
      setSetupIntentSecret(response.data.setupIntentSecret);
    } catch (err) {
      console.error('Error fetching setup intent:', err);
      toast.error('Failed to initialize payment form');
    }
  }, [companyId, getStripeCustomerSecrets]);

  useEffect(() => {
    if (showAddForm) {
      fetchSetupIntent();
    }
  }, [showAddForm, fetchSetupIntent]);

  const handleRemove = (paymentMethodId: string) => {
    // The one being removed is still in the list: a single card means this is it.
    const isOnlyPaymentMethod = paymentMethods.length === 1;
    const isDefaultMethod = paymentMethods.find(pm => pm.id === paymentMethodId)?.isDefault;

    openDialog({
      title: 'Remove Payment Method',
      content: isOnlyPaymentMethod
        ? 'Are you sure you want to remove this payment method? This is your only payment method and removing it will require you to add a new one to continue with your account setup.'
        : isDefaultMethod
        ? 'Are you sure you want to remove this payment method? This is currently set as your default payment method.'
        : 'Are you sure you want to remove this payment method?',
      onConfirm: async () => {
        try {
          await detachPaymentMethod(companyId, paymentMethodId);
          toast.success('Payment method removed successfully');
          await onPaymentMethodRemoved();
          setShowAddForm(paymentMethods.length === 1);
        } catch (err: unknown) {
          const errorMessage = err instanceof Error ? err.message : 'Failed to remove payment method';
          console.error('Error removing payment method:', err);
          toast.error(errorMessage);
        }
      },
      confirmButtonColor: 'error',
      cancelButtonColor: 'primary',
    });
  };

  const stripeElementsAppearance = useMemo(
    () => ({
      theme: 'stripe' as const,
      variables: {
        // Stripe brand purple — tracked separately (brand-color leak cleanup)
        colorPrimary: '#635bff',
        colorBackground: '#ffffff',
        colorText: '#1a1f36',
        colorDanger: '#df1b41',
        fontFamily: 'system-ui, sans-serif',
        spacingUnit: '4px',
        borderRadius: '6px',
      },
      rules: {
        '.Input': {
          border: `1px solid #e0e0e0`,
          borderRadius: '6px',
          padding: '12px',
        },
        '.Input:focus': {
          border: '1px solid #635bff',
          boxShadow: '0 0 0 3px rgba(99, 91, 255, 0.1)',
        },
        '.Tab': {
          border: `1px solid #e0e0e0`,
          borderRadius: '6px',
        },
        '.Tab--selected': {
          border: '1px solid #635bff',
          backgroundColor: '#f8f9ff',
        },
      },
    }),
    [theme],
  );

  const handleSetDefault = async (paymentMethodId: string) => {
    try {
      await setDefaultPaymentMethod(companyId, paymentMethodId);
      toast.success('Default payment method updated');
      onDefaultChanged();
    } catch (err: unknown) {
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to update default payment method';
      console.error('Error setting default payment method:', err);
      toast.error(errorMessage);
    }
  };


  return (
    <Box sx={{width: '100%'}}>
      {paymentMethods.length > 0 && (
        <Box sx={{ mb: 3 }}>
          <Box>
          {paymentMethods.map((pm) => (
            <PaymentMethodItem
              key={pm.id}
              paymentMethod={pm}
              isDefault={pm.isDefault}
              onRemove={handleRemove}
              onSetDefault={handleSetDefault}
              canRemove={true}
              locked={locked}
            />
          ))}
        </Box>
        {!showAddForm && !locked && <Button
          variant="outlined"
          startIcon={<AddIcon />}
          onClick={() => {
            onAddCardStarted?.();
            setShowAddForm(true);
          }}
          sx={{
            fontWeight: 'fontWeightMedium',
            visibility: loading ? 'hidden' : 'visible',
            fontSize: '14px',
          }}
        >
          Add payment method
        </Button>}
        </Box>
      )}

      {/* No inset of its own: the form stands where the cards above it stand, or the two do not line up. */}
      {showAddForm && !locked && setupIntentSecret && stripePromise &&
        <Box sx={{ width: '100%', mb: 3 }}>
          <Elements stripe={stripePromise} options={{
            clientSecret: setupIntentSecret,
            appearance: stripeElementsAppearance,
          }}>
            <AddPaymentMethodForm
              // No auto-advance: Stripe attaches the card asynchronously, and submitting the
              // setup in the same breath beats the backend to it ("Missing requirements:
              // add_payment"). The user's own click on Continue gives it the time it needs.
              // A host that passes `submit` opts out of that wait and must survive the race
              // itself — the signup checkout re-reads the card from Stripe before refusing.
              submit={submit}
              onCancel={paymentMethods.length > 0
                ? () => {
                  setShowAddForm(false);
                  setSetupIntentSecret(null);
                  onAddCardCancelled?.();
                }
                : undefined}
              onSuccess={async () => {
                setShowAddForm(false);
                setSetupIntentSecret(null);
                await onPaymentMethodAdded();
              }}
              onSetAsDefault={async (paymentMethodId: string) => {
                await setDefaultPaymentMethod(companyId, paymentMethodId);
              }}
              hasDefaultPaymentMethod={hasDefaultPaymentMethod}
              makeNewCardDefault={makeNewCardDefault}
            />
          </Elements>
        </Box>
      }
      <ConfirmationDialog />
    </Box>
  );
};

/**
 * Every screen that lets a company put a card on file: the onboarding wizard and the self-service signup
 * share this one. A cut-down copy per host is how one of them ends up showing an empty form to a client who
 * already has a card.
 *
 * Nothing about either host leaks in here: what the surrounding flow does after a card is added or removed
 * arrives as `onChanged`.
 */
export const PaymentMethods: React.FC<PaymentMethodsProps> = ({ companyId, onChanged, makeNewCardDefault, onAddCardStarted, onAddCardCancelled, locked, submit }) => {
  const [setupIntentSecret, setSetupIntentSecret] = useState<string | null>(null);
  const [paymentMethods, setPaymentMethods] = useState<StripePaymentMethod[]>([]);
  const [stripePublishableKey, setStripePublishableKey] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Held in a ref so a host that rebuilds the callback each render cannot restart the load.
  const onChangedRef = useRef(onChanged);
  onChangedRef.current = onChanged;

  const getStripeCustomerSecrets = useGetStripeCustomerSecretsApi();
  const getStripePublishableKey = useGetStripePublishableKeyApi();
  const getStripePaymentMethods = useGetStripePaymentMethodsApi();

  const fetchPaymentMethods = useCallback(async (): Promise<StripePaymentMethod[]> => {
    if (!companyId) return [];

    try {
      const response = await getStripePaymentMethods(companyId);
      setPaymentMethods(response.data);
      return response.data;
    } catch (err: unknown) {
      console.error('Error fetching payment methods:', err);
      toast.error('Failed to load payment methods');
      return [];
    }
  }, [companyId, getStripePaymentMethods]);

  // The list is refreshed first so the host is told what is on file now, not what was a moment ago.
  const handleChanged = useCallback(async (reason: PaymentMethodsChange) => {
    const methods = await fetchPaymentMethods();
    await onChangedRef.current?.({ methods: methods, reason: reason });
  }, [fetchPaymentMethods]);

  const fetchClientSecret = useCallback(async () => {
    if (!stripePublishableKey) return;

    try {
      setError(null);

      const response = await getStripeCustomerSecrets(companyId);
      setSetupIntentSecret(response.data.setupIntentSecret);
    } catch (err: unknown) {
      const errorMessage =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        'Failed to initialize payment';
      console.error('Error fetching clientSecret:', err);
      setError(errorMessage);
      toast.error(errorMessage);
      setLoading(false);
    }
  }, [companyId, stripePublishableKey, getStripeCustomerSecrets]);

  const fetchPublishableKey = useCallback(async () => {
    try {
      const response = await getStripePublishableKey(companyId);
      setStripePublishableKey(response.data.publishableKey);
    } catch (err: unknown) {
      const errorMessage =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        'Failed to load Stripe configuration';
      console.error('Error fetching publishable key:', err);
      setError(errorMessage);
      toast.error(errorMessage);
    }
  }, [companyId, getStripePublishableKey]);


  useEffect(() => {
    fetchPublishableKey();
  }, [fetchPublishableKey]);

  useEffect(() => {
    if (stripePublishableKey) {
      fetchClientSecret();
    }
  }, [stripePublishableKey, fetchClientSecret]);

  /**
   * The first load reports too, with no reason attached. A host that decides anything from the cards on
   * file — the signup funnel keeps its pay button shut until there is one — would otherwise wait forever
   * for a change that never comes, because the card was already there when the screen opened.
   */
  useEffect(() => {
    if (setupIntentSecret && companyId) {
      fetchPaymentMethods()
        .then(methods => onChangedRef.current?.({ methods: methods, reason: 'loaded' }))
        .finally(() => {
          setLoading(false);
        });
    }
  }, [setupIntentSecret, companyId, fetchPaymentMethods]);

  if (loading || !stripePublishableKey) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
        <CircularProgress />
        <Typography variant="body1" sx={{ ml: 2 }}>
          Initializing payment...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 4 }}>
        <Typography variant="h6" color="error" gutterBottom>
          Payment Initialization Failed
        </Typography>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          {error}
        </Typography>
        <Button variant="outlined" onClick={fetchClientSecret} sx={{ mt: 2 }}>
          Try Again
        </Button>
      </Box>
    );
  }

  if (!setupIntentSecret) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Typography variant="body1" gutterBottom>
          Click below to set up payment methods
        </Typography>
        <Button variant="contained" onClick={fetchClientSecret} sx={{ mt: 2 }}>
          Setup Payment Methods
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ position: 'relative', height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 3, justifyContent: 'center', alignItems: 'center', textAlign: 'center' }}>
        <PaymentMethodsList
                paymentMethods={paymentMethods}
                onDefaultChanged={() => handleChanged('default')}
                onPaymentMethodRemoved={() => handleChanged('removed')}
                onPaymentMethodAdded={() => handleChanged('added')}
                companyId={companyId}
                loading={loading}
                stripePublishableKey={stripePublishableKey}
                makeNewCardDefault={makeNewCardDefault}
                onAddCardStarted={onAddCardStarted}
                onAddCardCancelled={onAddCardCancelled}
                locked={locked}
                submit={submit}
              />
      </Box>
    </Box>
  );
};
